import { Button, Text } from "@ttc3k/trekker";
import { EditPencil, MinusCircle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { DraggableItem } from "shared/components/atoms";
import { Flex } from "styled-system/jsx";
import { cva } from "styled-system/css";

const containerRecipe = cva({
  base: {
    borderColor: "border.light",
    transition: "border-color 0.2s ease-in-out",
  },
  variants: {
    visual: {
      default: {
        borderColor: "border.light",
      },
      error: {
        borderColor: "border.error",
        outline: "1px solid",
        outlineColor: "border.error",
      },
    },
  },
});

type EventLocationCardProps = {
  index: number;
  title: string;
  address: string;
  addressError?: string;
  onEditClick: () => void;
  onDeleteClick?: () => void;
};

export const EventLocationCard = ({
  index,
  title,
  address,
  addressError,
  onEditClick,
  onDeleteClick,
}: EventLocationCardProps) => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_HOST.CARD",
  });

  const containerClasses = containerRecipe({
    visual: addressError ? "error" : "default",
  });

  return (
    <DraggableItem gap="150">
      <Flex
        flexDir="row"
        alignItems="stretch"
        gap="0"
        borderWidth="1px"
        borderRadius="100"
        className={containerClasses}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          width="70px"
          bgColor="var(--wa-colors-ink-100)"
          borderRight="1px solid"
          borderRightColor="border.light"
          borderTopLeftRadius="100"
          borderBottomLeftRadius="100"
        >
          <Text visual="smallCode" color="text.mid">
            {index + 1}
          </Text>
        </Flex>
        <Flex gap="300" py="250" pl="250" pr="300" width="full">
          <Flex
            flexDir="column"
            justifyContent="center"
            gap="50"
            minH="50px"
            width="full"
          >
            <Text visual="bodySemiBold" lineClamp={1} wordBreak="break-all">
              {title}
            </Text>
            {address && !addressError ? (
              <Text
                visual="smallMedium"
                color="text.light"
                lineClamp={1}
                wordBreak="break-all"
              >
                {address}
              </Text>
            ) : (
              <Text visual="smallMedium" color="text.error.dark">
                {addressError}
              </Text>
            )}
          </Flex>
          <Flex gap="150" color="text.light" alignItems="center">
            <Button
              visual="unstyled"
              Icon={MinusCircle}
              onClick={onDeleteClick}
              type="button"
              aria-label={t("DELETE")}
              color={{
                base: "icon.light",
                hover: "icon.mid",
              }}
              p="0"
            />
            |
            <Button
              visual="unstyled"
              Icon={EditPencil}
              onClick={onEditClick}
              type="button"
              aria-label={t("EDIT")}
              color={{
                base: "icon.light",
                hover: "icon.mid",
              }}
              p="0"
            />
          </Flex>
        </Flex>
      </Flex>
    </DraggableItem>
  );
};
